<script setup>
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { resolveProductsOrdersConfig } from 'dashboard/helper/productsOrdersFeatures';

const props = defineProps({
  currentChat: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const { t } = useI18n();
const { currentAccount } = useAccount();
const dialogRef = ref(null);

const products = useMapGetter('productsOrders/getProducts');
const uiFlags = useMapGetter('productsOrders/getUIFlags');
const productsOrdersConfig = computed(() =>
  resolveProductsOrdersConfig(currentAccount.value?.settings)
);

const buildDefaultForm = () => ({
  productId: '',
  productVariantId: '',
  quantity: 1,
  notes: '',
});

const form = reactive(buildDefaultForm());

const sender = computed(() => props.currentChat?.meta?.sender || {});
const isSubmitting = computed(() => uiFlags.value.isCreatingProductOrder);
const selectedProduct = computed(() =>
  products.value.find(product => Number(product.id) === Number(form.productId))
);
const activeVariants = computed(() =>
  (selectedProduct.value?.productVariants || []).filter(variant => variant.active)
);
const selectedVariant = computed(() =>
  activeVariants.value.find(
    variant => Number(variant.id) === Number(form.productVariantId)
  )
);
const requiresVariant = computed(() => activeVariants.value.length > 0);
const selectedInventoryItem = computed(
  () => selectedVariant.value || selectedProduct.value
);
const availableStock = computed(() =>
  Number(
    selectedVariant.value?.stockQuantity ??
      selectedProduct.value?.availableStock ??
      selectedProduct.value?.stockQuantity ??
      0
  )
);
const allowBackorder = computed(() => !!selectedProduct.value?.allowBackorder);
const trackStock = computed(() => !!selectedProduct.value?.trackStock);
const stockBlocked = computed(
  () =>
    trackStock.value &&
    !allowBackorder.value &&
    availableStock.value < Number(form.quantity || 1)
);
const canSubmit = computed(
  () =>
    !!form.productId &&
    (!requiresVariant.value || !!form.productVariantId) &&
    !stockBlocked.value
);

const inventoryStatusLabel = computed(() => {
  const status = selectedInventoryItem.value?.inventoryStatus;
  if (!status) return '';

  return t(`PRODUCTS_ORDERS.INVENTORY.STATUS.${status.toUpperCase()}`);
});

watch(
  () => form.productId,
  () => {
    form.productVariantId = '';
  }
);

const resetForm = () => {
  Object.assign(form, buildDefaultForm());
};

const ensureProductsLoaded = async () => {
  await store.dispatch('productsOrders/getProducts');
};

const open = async () => {
  resetForm();
  await ensureProductsLoaded();
  await nextTick();
  dialogRef.value?.open?.();
};

const close = () => {
  dialogRef.value?.close?.();
  resetForm();
};

const buildAddressFromSender = () => {
  return (
    sender.value?.additional_attributes?.address ||
    sender.value?.additional_attributes?.address_line_1 ||
    ''
  );
};

const buildLocationFromSender = () => {
  return sender.value?.additional_attributes?.location || '';
};

const buildDefaultActionsPayload = () => {
  const productDefaultActions = Array.isArray(
    selectedProduct.value?.automationRules?.defaultActions
  )
    ? selectedProduct.value.automationRules.defaultActions
    : [];
  const defaultActions = productDefaultActions.length
    ? productDefaultActions
    : Array.isArray(productsOrdersConfig.value.defaultActions)
      ? productsOrdersConfig.value.defaultActions
      : [];

  return defaultActions
    .filter(action => {
      const actionType = action.actionType || action.action_type || 'manual';
      const hasName = !!action.name?.trim?.();
      if (!hasName) return false;
      if (actionType === 'message') return !!action.content?.trim?.();
      if (actionType === 'template') {
        return !!(
          action.templateKey ||
          action.templatePayload?.name ||
          action.template_payload?.name
        );
      }

      return true;
    })
    .map((action, index) => ({
      position: index + 1,
      name: action.name,
      action_type: action.actionType || action.action_type || 'manual',
      content: action.content || '',
      status: 'pending',
      trigger_status: action.triggerStatus || action.trigger_status || 'manual',
      complete_order: !!(action.completeOrder ?? action.complete_order),
      template_payload: action.templatePayload || action.template_payload || {},
    }));
};

const handleConfirm = async () => {
  if (!canSubmit.value) {
    return;
  }

  try {
    await store.dispatch('productsOrders/createProductOrder', {
      product_id: form.productId,
      product_variant_id: form.productVariantId || null,
      conversation_id: props.currentChat?.id || null,
      contact_id: sender.value?.id || null,
      contact_name:
        sender.value?.name ||
        sender.value?.email ||
        sender.value?.phone_number ||
        t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE'),
      contact_phone: sender.value?.phone_number || '',
      contact_email: sender.value?.email || '',
      address: buildAddressFromSender(),
      location: buildLocationFromSender(),
      quantity: Number(form.quantity || 1),
      source: 'conversation',
      status: 'pending',
      notes: form.notes.trim(),
      product_order_actions_attributes: buildDefaultActionsPayload(),
    });
    useAlert(t('PRODUCTS_ORDERS.ALERTS.ORDER_CREATED'));
    close();
  } catch (error) {
    // Store toast handles API errors
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    :title="$t('PRODUCTS_ORDERS.QUICK_CREATE.TITLE')"
    :description="$t('PRODUCTS_ORDERS.QUICK_CREATE.DESCRIPTION')"
    :confirm-button-label="$t('PRODUCTS_ORDERS.QUICK_CREATE.CONFIRM')"
    :is-loading="isSubmitting"
    :disable-confirm-button="!canSubmit"
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <div class="grid gap-4">
      <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
        <p class="mb-1 text-sm font-medium text-n-slate-12">
          {{ sender.name || sender.email || sender.phone_number }}
        </p>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ sender.phone_number || sender.email || '' }}
        </p>
      </div>

      <div class="grid gap-4 md:grid-cols-2">
        <div class="flex min-w-0 flex-col gap-1 md:col-span-2">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ $t('PRODUCTS_ORDERS.FIELDS.PRODUCT') }}
          </label>
          <select
            v-model="form.productId"
            class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
          >
            <option value="">
              {{ $t('PRODUCTS_ORDERS.QUICK_CREATE.SELECT_PRODUCT') }}
            </option>
            <option
              v-for="product in products"
              :key="product.id"
              :value="product.id"
            >
              {{ product.name }}
            </option>
          </select>
          <p
            v-if="!products.length"
            class="mb-0 text-xs text-n-slate-10"
          >
            {{ $t('PRODUCTS_ORDERS.QUICK_CREATE.NO_PRODUCTS') }}
          </p>
        </div>

        <div
          v-if="requiresVariant"
          class="flex min-w-0 flex-col gap-1 md:col-span-2"
        >
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ $t('PRODUCTS_ORDERS.FIELDS.VARIANT') }}
          </label>
          <select
            v-model="form.productVariantId"
            class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
          >
            <option value="">
              {{ $t('PRODUCTS_ORDERS.QUICK_CREATE.SELECT_VARIANT') }}
            </option>
            <option
              v-for="variant in activeVariants"
              :key="variant.id"
              :value="variant.id"
            >
              {{ variant.name }}
            </option>
          </select>
        </div>

        <Input
          v-model="form.quantity"
          type="number"
          min="1"
          :label="$t('PRODUCTS_ORDERS.FIELDS.QUANTITY')"
        />
      </div>

      <div
        v-if="selectedProduct"
        class="rounded-xl border border-n-weak bg-n-alpha-1 p-4"
      >
        <div class="flex flex-wrap items-center gap-2">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ selectedVariant?.name || selectedProduct.name }}
          </p>
          <span class="rounded-full bg-n-solid-1 px-2 py-1 text-xs text-n-slate-11">
            {{ inventoryStatusLabel }}
          </span>
        </div>
        <p class="mb-0 mt-2 text-sm text-n-slate-11">
          {{
            trackStock
              ? $t('PRODUCTS_ORDERS.INVENTORY.AVAILABLE_STOCK', {
                  count: availableStock,
                })
              : $t('PRODUCTS_ORDERS.INVENTORY.NOT_TRACKED')
          }}
        </p>
        <p
          v-if="stockBlocked"
          class="mb-0 mt-2 text-sm text-n-ruby-11"
        >
          {{ $t('PRODUCTS_ORDERS.INVENTORY.OUT_OF_STOCK_BLOCKED') }}
        </p>
      </div>

      <TextArea
        v-model="form.notes"
        :label="$t('PRODUCTS_ORDERS.FIELDS.NOTES')"
        :max-length="1000"
      />
    </div>
  </Dialog>
</template>
